import { useEffect, useState } from "react";
import { isEmpty } from "remeda";

import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ImportantNote } from "@/components/ui/importantNote";
import { Typography } from "@/components/ui/typography";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const testingRequirementOptions = [
  { label: "PT", value: "pt" },
  { label: "PWHT", value: "pwht" },
  { label: "PMI", value: "pmi" },
  { label: "RT", value: "rt" },
  { label: "UT", value: "ut" },
  { label: "MT", value: "mt" },
  { label: "Dye Check", value: "dye_check" },
];

const cardName = "postConstructionTestingRequirements";

export function PostConstructionTestingRequirements() {
  const { sections, toggleExpandableSection, setPostConstructionTestingRequirements } = useCUMechanicalJobStore();
  const savedRequirements = sections.postConstructionTestingRequirements;
  const [selectedRequirements, setSelectedRequirements] = useState<Array<string>>([]);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (isEmpty(selectedRequirements) && savedRequirements.isDataFilled) {
      setSelectedRequirements(savedRequirements.data.post_construction_testing_requirements || []);
    }
  }, [
    savedRequirements.data.post_construction_testing_requirements,
    savedRequirements.isDataFilled,
    selectedRequirements,
  ]);

  useEffect(() => {
    if (!isEmpty(selectedRequirements) && showError) {
      setShowError(false);
    }
  }, [selectedRequirements, showError]);

  const handleToggleRequirement = (value: string, checked: boolean) => {
    if (value === "none") {
      setSelectedRequirements(checked ? ["none"] : []);
      return;
    }

    setSelectedRequirements((prev) => {
      const withoutNone = prev.filter((option) => option !== "none");
      if (checked) {
        return [...withoutNone, value];
      }
      return withoutNone.filter((option) => option !== value);
    });
  };

  const handleSave = () => {
    if (isEmpty(selectedRequirements)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setPostConstructionTestingRequirements({
      data: { post_construction_testing_requirements: selectedRequirements },
      status: "done",
    });
  };

  return (
    <ExpandableSection
      status={savedRequirements.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Post Construction Testing Requirements"
    >
      <div className="flex flex-col">
        <Typography variant="text-lg" weight="semibold" className="text-gray-900">
          Check any that apply
        </Typography>
        <div className="my-6 flex flex-col gap-4">
          {testingRequirementOptions.map((option) => (
            <Checkbox
              key={option.value}
              checked={selectedRequirements.includes(option.value)}
              onChange={(checked) => handleToggleRequirement(option.value, checked)}
              label={option.label}
            />
          ))}
          <Checkbox
            checked={selectedRequirements.includes("none")}
            onChange={(checked) => handleToggleRequirement("none", checked)}
            label="None of the above"
          />
        </div>
        {showError && <ImportantNote>Select at least one testing requirement</ImportantNote>}
        <Button onClick={handleSave} className="self-end">
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
